import React, { useMemo, useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";

// =============================================================
// ReassignMemberModal — pick any vacant slot in the tree to move
// the current occupant into. The server closes the source
// occupancy and opens a new one atomically.
// =============================================================
export default function ReassignMemberModal({ node, member, allNodes, onConfirm, onClose, submitting }) {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const vacants = useMemo(() => {
    const list = (allNodes || []).filter(n => !n.memberId && n.status === "Vacant" && n.nodeId !== node.nodeId);
    const term = q.trim().toLowerCase();
    return list.filter(n => {
      if (!term) return true;
      return (n.slotRef || "").toLowerCase().includes(term) || (n.levelName || "").toLowerCase().includes(term);
    }).sort((a, b) => (a.slotRef || "").localeCompare(b.slotRef || ""));
  }, [allNodes, node, q]);

  const displayName = member
    ? ((member.firstName || "") + " " + (member.lastName || "")).trim()
    : "Occupant";

  return (
    <div className="modal-overlay" data-testid="reassign-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Reassign Member</div>
          <button className="modal-close" onClick={onClose} data-testid="reassign-close" aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, marginBottom: 14 }}>
            {member?.photo ? (
              <img src={member.photo} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                {(member?.firstName?.[0] || "?") + (member?.lastName?.[0] || "")}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kc-text)" }}>{displayName}</div>
              <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>
                Currently at <strong style={{ color: "var(--kc-accent)" }}>{node.slotRef}</strong> · {node.levelName}
              </div>
            </div>
          </div>

          <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Pick a vacant slot</label>
          <input
            data-testid="reassign-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by slot ref or level (e.g. GA, Beta)…"
            className="form-input"
            style={{ width: "100%", marginTop: 6, marginBottom: 12 }}
          />
          <div data-testid="reassign-slots" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto", paddingRight: 4 }}>
            {vacants.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: "var(--kc-sub)", fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>
                No vacant slots available. Create a child slot first.
              </div>
            )}
            {vacants.map(n => {
              const isSel = selectedId === n.nodeId;
              return (
                <button
                  key={n.nodeId}
                  type="button"
                  data-testid={`reassign-slot-${n.slotRef}`}
                  onClick={() => setSelectedId(n.nodeId)}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                    padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                    background: isSel ? "color-mix(in srgb, var(--kc-accent) 15%, transparent)" : "transparent",
                    border: `1px solid ${isSel ? "var(--kc-accent)" : "var(--kc-sub)"}`,
                    textAlign: "left", color: "inherit", font: "inherit",
                  }}
                >
                  <div>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, fontWeight: 700, color: "var(--kc-accent)" }}>{n.slotRef}</div>
                    <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>{n.levelName} · Level {n.level}</div>
                  </div>
                  <CustomIcon name="arrow-right" size={12} />
                </button>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} data-testid="reassign-cancel">Cancel</button>
          <button
            type="button"
            className="btn btn-gold btn-sm"
            data-testid="reassign-confirm"
            disabled={!selectedId || submitting}
            onClick={() => {
              const dst = (allNodes || []).find(n => n.nodeId === selectedId);
              if (dst) onConfirm(dst);
            }}
          >{submitting ? "Reassigning…" : "Confirm Reassignment"}</button>
        </div>
      </div>
    </div>
  );
}
